import { Link } from 'react-router-dom'
import { Mail, Send, Sparkles, ShieldCheck } from 'lucide-react'
import { Panel, Chip } from '../../ds/index'
import { Icon } from '../../components/Icon'
import { useApi } from '../../lib/useApi'
import { ago } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { CardPayments } from '../Pricing'

type MailboxConn = { ready: boolean; inbox: { address: string; status: string; last_polled_at: string | null } | null; missing?: string[] }
type Conn = {
  demo_data: boolean
  gmail: MailboxConn
  zoho: MailboxConn
  email: { ready: boolean; from: string | null; sent_30d: number; failed_30d: number; missing?: string[] }
  ai: { adapter: string; live: boolean }
  public_url?: string
}

export default function Connections() {
  const { data, error } = useApi<Conn>('/api/connections')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  // The mailbox reps' alerts arrive in: Zoho first, then Gmail.
  const [mName, g] = data.zoho.inbox ? ['Zoho Mail', data.zoho] as const : data.gmail.inbox ? ['Gmail', data.gmail] as const
    : data.zoho.ready ? ['Zoho Mail', data.zoho] as const : ['Gmail', data.gmail] as const
  const missing = [...new Set([...(data.zoho.missing || []), ...(data.gmail.missing || [])])]
  const e = data.email

  const card = (icon: typeof Mail, title: string, state: { label: string; tone?: 'pos' | 'warn' | 'neg' }, body: React.ReactNode, missing?: string[]) => (
    <Panel>
      <div className="conn-card">
        <span className="conn-icon"><Icon of={icon} /></span>
        <div className="conn-body">
          <div className="spread"><h2 className="panel-title">{title}</h2><Chip tone={state.tone} dot>{state.label}</Chip></div>
          <div className="conn-text">{body}</div>
          {missing && missing.length > 0 && <div className="mailbox-spec"><span className="view-as-section-title">SPEC only</span><p>Missing on the server: {missing.map((m) => <code key={m}>{m}</code>)}</p></div>}
        </div>
      </div>
    </Panel>
  )

  return (
    <>
    <div style={{ marginBottom: 'var(--s-6)' }}><CardPayments /></div>
    <div className="conn-grid">
      {card(Mail, `${mName} mailbox`,
        g.inbox && g.inbox.status === 'active' ? { label: 'Reading', tone: 'pos' } : g.inbox?.status === 'needs_reconnect' ? { label: 'Needs reconnecting', tone: 'neg' } : g.ready ? { label: 'Not connected', tone: 'warn' } : { label: 'Not switched on' },
        g.inbox ? <>Reading <strong>{g.inbox.address}</strong>{g.inbox.last_polled_at ? `, checked ${ago(g.inbox.last_polled_at)}` : ''}. <Link to="/mailbox">Mailbox</Link></>
          : <>Where reps' Muse alerts arrive. {g.ready ? 'Ready to connect on the ' : 'Switched on by SPEC once the mailbox exists. Set up on the '}<Link to="/mailbox">Mailbox</Link> page.</>,
        data.zoho.ready || data.gmail.ready ? undefined : missing)}
      {card(Send, 'Subscriber emails',
        e.ready ? { label: 'Sending', tone: 'pos' } : { label: 'Not set up' },
        e.ready ? <>From <strong>{e.from}</strong>. {e.sent_30d} sent in 30 days{e.failed_30d ? `, ${e.failed_30d} failed` : ''}. <Link to="/settings?tab=emails">Email settings</Link></>
          : <>Emails are written and recorded as not sent until sending is set up on {`the company's`} own email domain. <Link to="/settings?tab=emails">Email settings</Link></>,
        e.missing)}
      {card(Sparkles, 'AI screening',
        data.ai.live ? { label: 'Live', tone: 'pos' } : data.ai.adapter === 'fake' ? { label: 'Simulated', tone: 'warn' } : { label: 'Off', tone: 'neg' },
        data.ai.live ? 'Every new post is read by AI to decide whether it is a request, and leads are routed to reps.'
          : data.ai.adapter === 'fake' ? 'Keyword rules stand in for the AI on this install.' : 'New posts wait in the queue until AI is set up.')}
      {card(ShieldCheck, 'Facebook', { label: 'Emails only', tone: 'pos' },
        <>Nothing here logs into Facebook or opens facebook.com. Reps reply from their own accounts; the platform only reads the notification emails.{data.demo_data ? ' This install holds invented demo records.' : ''}</>)}
    </div>
    </>
  )
}
